<template>
  <Teleport to="body">
    <div class="modal-backdrop" @click.self="$emit('close')">
      <div class="modal-container vendor-code-modal">
        <div class="modal-content">
          <div class="modal-header">
            <div class="modal-title-section">
              <h2 class="modal-title">{{ $t('modals.missingVendorCodes') }}</h2>
              <p class="modal-subtitle">{{ $t('modals.missingVendorCodesSubtitle') }}</p>
            </div>
            <button @click="$emit('close')" class="btn btn-icon">
              <LineIcon name="x" :size="15" :stroke-width="1.8" />
            </button>
          </div>

          <div class="modal-body">
            <div v-if="loading" class="loading-container">
              <div class="spinner-small"></div>
              <span>{{ $t('modals.saving') }}</span>
            </div>

            <div v-else class="vendors-list">
              <div v-for="vendor in vendors" :key="vendor.id" class="vendor-item">
                <div class="vendor-info">
                  <div class="vendor-name">{{ vendor.vendor_name }}</div>
                  <div class="vendor-vat">{{ vendor.vendor_vat || $t('modals.noVAT') }}</div>
                </div>
                <div class="vendor-code-input">
                  <input
                    v-model="vendor.vendor_code"
                    type="text"
                    :placeholder="$t('modals.enterVendorCode')"
                    class="form-control"
                    @keypress.enter="saveVendorCodes"
                  />
                </div>
              </div>
            </div>

            <div v-if="error" class="error-message">
              {{ error }}
            </div>
          </div>

          <div class="modal-footer">
            <button @click="$emit('close')" class="btn btn-secondary">
              {{ $t('modals.cancel') }}
            </button>
            <button @click="saveVendorCodes" class="btn btn-primary" :disabled="loading || !hasChanges">
              <font-awesome-icon v-if="loading" icon="fa-solid fa-spinner" spin />
              {{ $t('modals.saveAndExport') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import LineIcon from '@/components/icons/LineIcon.vue';
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import api from '@/utils/api';

const { t } = useI18n();

const props = defineProps({
  vendors: {
    type: Array,
    required: true
  }
});

const emit = defineEmits(['close', 'saved']);

const loading = ref(false);
const error = ref(null);

const hasChanges = computed(() => {
  return props.vendors.every(vendor => vendor.vendor_code && vendor.vendor_code.trim() !== '');
});

const saveVendorCodes = async () => {
  // Check if all vendors have codes
  const missingCodes = props.vendors.filter(v => !v.vendor_code || v.vendor_code.trim() === '');
  if (missingCodes.length > 0) {
    error.value = `Please add codes for all ${missingCodes.length} vendor(s)`;
    return;
  }

  loading.value = true;
  error.value = null;

  try {
    // Save each vendor code
    const promises = props.vendors.map(vendor => 
      api.put(`/api/vendors/${vendor.id}`, {
        vendor_name: vendor.vendor_name,
        vendor_vat: vendor.vendor_vat,
        vendor_code: vendor.vendor_code
      })
    );

    await Promise.all(promises);
    
    emit('saved');
  } catch (err) {
    console.error('Failed to save vendor codes:', err);
    
    // Display specific error message for vendor_code
    if (err.response?.data?.errors?.vendor_code) {
      error.value = err.response.data.errors.vendor_code[0];
    } else if (err.response?.data?.message) {
      error.value = err.response.data.message;
    } else {
      error.value = 'Failed to save vendor codes. Please try again.';
    }
  } finally {
    loading.value = false;
  }
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9998;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-container {
  position: relative;
  z-index: 9999;
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
}

.vendor-code-modal {
  max-width: 700px;
}

.modal-subtitle {
  font-size: 14px;
  color: #6c757d;
  margin-top: 4px;
}

.vendors-list {
  max-height: 400px;
  overflow-y: auto;
}

.vendor-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border-bottom: 1px solid #e9ecef;
  gap: 16px;
}

.vendor-item:last-child {
  border-bottom: none;
}

.vendor-info {
  flex: 1;
  min-width: 0;
}

.vendor-name {
  font-weight: 500;
  color: #212529;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vendor-vat {
  font-size: 13px;
  color: #6c757d;
  margin-top: 2px;
}

.vendor-code-input {
  flex: 0 0 200px;
}

.vendor-code-input input {
  width: 100%;
  padding: 6px 12px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 14px;
}

.vendor-code-input input:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.error-message {
  background-color: #f8d7da;
  color: #721c24;
  padding: 12px;
  border-radius: 4px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.loading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 40px;
  color: #6c757d;
}

.spinner-small {
  width: 20px;
  height: 20px;
  border: 2px solid #f3f3f3;
  border-top: 2px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #0056b3;
}

.btn-primary:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* ── The dashboard's dress ─────────────────────────────────────────────
 *
 * One card, one 12px radius, hairline edges, and controls at the table's
 * scale: 13px labels in 32px boxes, one filled button and the rest quiet.
 * A dialogue is opened from the table and should read as the same product.
 *
 * Last in the file on purpose — these do not delete what the modal set for
 * itself above, they settle the frame, the heading and the buttons. */
.modal-content,
.modal-container,
.modal-content-large {
    border: 1px solid #dde3ea;
    border-radius: 12px;
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
}

.modal-header {
    padding: 12px 14px;
    border-bottom: 1px solid #dde3ea;
    background: #fff;
}

.modal-header h2,
.modal-header h3,
.modal-header h4,
.modal-title {
    margin: 0;
    font-size: 14px;
    font-weight: 700;
    color: #1e293b;
}

.modal-subtitle {
    margin: 1px 0 0;
    font-size: 11px;
    font-weight: 400;
    color: #64748b;
}

.modal-body {
    padding: 12px 14px;
}

.modal-footer {
    padding: 10px 14px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid #dde3ea;
}

/* The close: a mark on its own, with the lift the rest of the app has. */
.modal-header .btn-icon,
.btn-close,
.modal-close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    min-width: 26px;
    padding: 0;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 8px;
    color: #64748b;
    box-shadow: none;
    transition: all 0.15s ease;
}

.modal-header .btn-icon:hover,
.btn-close:hover,
.modal-close:hover {
    background: transparent;
    color: #0697e4;
    transform: translateY(-2px);
}

/* The buttons: the same box everywhere, filled only where something is
   being committed. */
.btn-primary,
.btn-secondary,
.btn-cancel,
.btn-cancel-edit,
.btn-save,
.btn-save-edit,
.btn-confirm,
.btn-danger,
.btn-success,
.btn-add {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    height: 32px;
    padding: 0 14px;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    line-height: 1;
    box-shadow: none;
    transition: all 0.15s ease;
}

.btn-secondary,
.btn-cancel,
.btn-cancel-edit {
    background: #fff;
    border: 1px solid #dde3ea;
    color: #1e293b;
}

.btn-secondary:hover:not(:disabled),
.btn-cancel:hover:not(:disabled),
.btn-cancel-edit:hover:not(:disabled) {
    background: #fff;
    border-color: #0697e4;
    color: #0697e4;
    box-shadow: none;
    transform: none;
}

.btn-primary,
.btn-confirm,
.btn-save,
.btn-save-edit,
.btn-add {
    background: #0697e4;
    border: 1px solid #0697e4;
    color: #fff;
}

.btn-primary:hover:not(:disabled),
.btn-confirm:hover:not(:disabled),
.btn-save:hover:not(:disabled),
.btn-save-edit:hover:not(:disabled),
.btn-add:hover:not(:disabled) {
    background: #0b84c7;
    border-color: #0b84c7;
    box-shadow: none;
    transform: none;
}

.btn-success {
    background: #10b981;
    border: 1px solid #10b981;
    color: #fff;
}

.btn-success:hover:not(:disabled) {
    background: #0ea371;
    border-color: #0ea371;
    box-shadow: none;
    transform: none;
}

.btn-danger {
    background: #e04545;
    border: 1px solid #e04545;
    color: #fff;
}

.btn-danger:hover:not(:disabled) {
    background: #c93a3a;
    border-color: #c93a3a;
    box-shadow: none;
    transform: none;
}

/* ── Fields at the table's scale ───────────────────────────────────────
 *
 * The dialogues were built with 16px type in 44px boxes, which next to the
 * 13px rows of the table reads as a different application. Same hairline,
 * same 8px corner, same type size as the controls on the dashboard. */
.modal-body input[type="text"],
.modal-body input[type="number"],
.modal-body input[type="email"],
.modal-body input[type="tel"],
.modal-body input[type="url"],
.modal-body input[type="search"],
.modal-body input[type="date"],
.modal-body input[type="password"],
.modal-body select,
.modal-body textarea {
    padding: 7px 10px;
    border: 1px solid #dde3ea;
    border-radius: 8px;
    font-family: inherit;
    font-size: 13px;
    color: #1e293b;
    background: #fff;
    box-shadow: none;
}

.modal-body input[type="text"]:focus,
.modal-body input[type="number"]:focus,
.modal-body input[type="email"]:focus,
.modal-body input[type="tel"]:focus,
.modal-body input[type="url"]:focus,
.modal-body input[type="search"]:focus,
.modal-body input[type="date"]:focus,
.modal-body input[type="password"]:focus,
.modal-body select:focus,
.modal-body textarea:focus {
    outline: none;
    border-color: #0697e4;
    box-shadow: none;
}

.modal-body label,
.modal-body .form-label {
    font-size: 11px;
    font-weight: 600;
    color: #64748b;
}

/* The corners.
 *
 * The card is rounded but does not clip, so the header's own white — and the
 * footer's — was painted square and stood a pixel or two outside the curve.
 * The bands carry the same radius on the side they sit on. Clipping the card
 * instead would cut off anything a dialogue drops below its edge. */
.modal-header {
    border-radius: 12px 12px 0 0;
}

.modal-footer,
.modal-actions {
    border-radius: 0 0 12px 12px;
}

/* ── One hover for a mark on its own ───────────────────────────────────
 *
 * Wherever a button is just an icon — a row's edit or delete, a field's
 * helper, a dialogue's close — hovering turns the mark its own colour and
 * lifts it two pixels. Nothing is drawn behind it. Written last, and the
 * danger variant written with both of its classes, because the rules being
 * answered are themselves .btn-icon.btn-icon-danger. */
.btn-icon:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: var(--primary, #0697e4);
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon.btn-icon-danger:hover,
.btn-icon.btn-icon-danger:hover:not(:disabled) {
  background: transparent;
  border-color: transparent;
  color: #e04545;
  box-shadow: none;
  transform: translateY(-2px);
}

.btn-icon svg {
  transition: transform 0.15s ease;
}

/* ── One frame, not two ────────────────────────────────────────────────
 *
 * The chrome above gives a hairline, a 12px corner and a shadow to both
 * .modal-content and .modal-container — but in this dialogue the container
 * is only the box that centres the card, and in one case it is far wider
 * than the card, so its edge was drawn as a rectangle standing in the empty
 * space beside the dialogue. The card keeps the frame; the wrapper carries
 * none. */
.modal-container {
  background: transparent;
  border: none;
  border-radius: 0;
  box-shadow: none;
}
</style>
